import TRUNCATE_UNITS from '@/utils/enum/TRUNCATE_UNITS';
import { formatBool, formatArrayToString } from '@/utils/shared';
import dateFormatter from '@/services/formatter_services/date-formatter';
import dateService from '@/services/date-service';
import { formatAttributeValues } from '@/services/format-attribute-value-service';
import { presentNumberToCurrencyTable } from '@/services/formatter_services/currency-formatter';

const getQuotationVersionFromReferenceNumber = (referenceNumber) => {
  return referenceNumber && referenceNumber.split('.').length == 3 ? referenceNumber.split('.')[2] : "";
};

const INQUIRY_HEADERS = Object.freeze([
  { text: 'Dashboard.widgets.inquiries.headers.id', value: 'id' },
  { text: 'Common.type', value: 'inquiryType' },
  {
    text: 'Common.label',
    value: 'requestIdentification',
    truncateUnit: TRUNCATE_UNITS.STRING,
    truncateAfter: 20
  },
  {
    text: 'Common.company',
    value: 'company',
    truncateUnit: TRUNCATE_UNITS.STRING,
    truncateAfter: 20
  },
  { text: 'Common.customer', value: 'customer' },
  { text: 'Dashboard.widgets.inquiries.headers.processOwner', value: 'processOwner' },
  {
    text: 'Common.status',
    value: 'status'
  },
  {
    text: 'Dashboard.widgets.inquiries.headers.inquiryCreated',
    value: 'inquiryCreated',
    presentWith: dateFormatter.formatDateToLocaleDateStringNoTime
  },
  {
    text: 'Dashboard.widgets.inquiries.headers.deadline',
    value: 'deadline',
    presentWith: dateFormatter.formatDateToLocaleDateStringNoTime
  },
  {
    text: 'Dashboard.widgets.inquiries.headers.updatedAt',
    value: 'updatedAt',
    presentWith: dateFormatter.formatDateToLocaleDateStringNoTime
  }
]);
const PROJECT_MANAGEMENT_WIDGETS = Object.freeze({
  PROJECT_WIDGETS: [
    {
      id: 'projectsOverview',
      moduleId: 'projectsOverview',
      path: 'projects',
      type: 'TABLE',
      getData: response => response.data.data?.map(data => data.data),
      headers: [
        {
          text: 'Common.matchcode',
          value: 'matchcode',
          truncateUnit: TRUNCATE_UNITS.STRING,
          truncateAfter: 10,
        },
        {
          text: 'Common.label',
          value: 'label',
          truncateUnit: TRUNCATE_UNITS.STRING,
          truncateAfter: 25,
        },
        {
          text: 'Common.companyLocation',
          value: 'location'
        },
        {
          text: 'Common.projectManager',
          value: 'projectManager'
        },
        {
          text: 'Projects.words.customerContact',
          value: 'customer',
        },
        {
          text: 'Projects.words.requestLeadType',
          value: 'requestLeadType'
        },
        {
          text: 'Projects.table.headers.projectStart',
          value: 'startDate',
          presentWith: dateFormatter.formatDateToLocaleDateStringNoTime
        },
        {
          text: 'Projects.words.orderNumberCustomer',
          value: 'orderNumberCustomer'
        },
        {
          text: 'Projects.table.headers.pickupDate',
          value: 'pickupDate',
          presentWith: dateFormatter.formatDateToLocaleDateStringNoTime
        },
        {
          text: 'Projects.table.headers.plannedEnd',
          value: 'endDateTarget',
          presentWith: dateFormatter.formatDateToLocaleDateStringNoTime
        },
        {
          text: 'Projects.table.headers.actualEnd',
          value: 'projectEndActual',
          presentWith: dateFormatter.formatDateToLocaleDateStringNoTime
        },        {
          text: 'Common.productivity',
          value: 'productivity'
        },
        {
          text: 'Projects.table.headers.projectPriority',
          value: 'priority'
        },
        {
          text: 'Common.characteristics',
          value: 'attributeNames',
          truncateUnit: TRUNCATE_UNITS.ARRAY,
          truncateAfter: 3,
        },
        {
          text: 'Common.attributes',
          value: 'attributeValues',
          presentWith: formatAttributeValues,
          truncateUnit: TRUNCATE_UNITS.ARRAY,
          truncateAfter: 3,
        },
        {
          text: 'Projects.table.headers.deleted',
          value: 'isDeleted',
          presentWith: formatBool,
        },
        {
          text: 'Common.participant',
          value: 'participants',
          truncateUnit: TRUNCATE_UNITS.ARRAY,
          truncateAfter: 2,
        },
        {
          text: 'Projects.sentences.currentWorkflowStep',
          value: 'currentStep',
        },
        {
          text: 'Projects.sentences.currentWorkflowAction',
          value: 'currentAction'
        },
        {
          text: 'Common.assignedEmployee',
          value: 'currentStepEmployee',
        },
        {
          text: 'Projects.table.headers.projectStatus',
          value: 'projectStatus'
        },
        {
          text: 'Common.host',
          value: 'host'
        },
        {
          text: 'sentences.vosDate',
          value: 'onSiteDate',
          presentWith: dateFormatter.formatDateToLocaleDateStringNoTime
        },
        {
          text: 'Common.car',
          value: 'onSiteTourLabel',
        },
        {
          text: 'Common.daysSinceLastAction',
          value: 'lastActionDate',
          presentWith: dateFormatter.daysSinceDate,
        },
        {
          text: 'Common.lastSentQuotation',
          value: 'lastQuotationSent',
          presentWith: dateFormatter.formatDateToLocaleDateStringNoTime,
        },
        {
          text: 'Common.lastQuotationVersion',
          value: "lastQuotationReferenceNumber",
          presentWith: getQuotationVersionFromReferenceNumber,
        },
        {
          text: 'Common.lastQuotationConfirmation',
          value: 'lastQuotationConfirmation',
          presentWith: dateFormatter.formatDateToLocaleDateStringNoTime,
        },
        {
          text: 'Common.device',
          value: 'device'
        },
        {
          text: 'Common.serialNumber',
          value: 'deviceManufacturerSerialNumber'
        },

      ],
      withRouterActionLink: true
    },
    {
      id: 'projectsAmount',
      path: 'projects',
      type: 'VALUE',
      defaultFilter: { requestType: 'COUNT' },
      getData: response => response.data.totalHits,
      moduleId: 'projectsOverview'
    }
  ],
  VOS_PROJECT_WIDGETS: [
    {
      id: 'vosProjectsOverview',
      moduleId: 'VOSProjectsOverview',
      customRouterLink: '/projects',
      path: 'projects?vosMode=true',
      type: 'TABLE',
      getData: response => response.data.data?.map(data => data.data),
      headers: [
        {
          text: 'Common.matchcode',
          value: 'matchcode',
          truncateUnit: TRUNCATE_UNITS.STRING,
          truncateAfter: 10
        },
        {
          text: 'Dashboard.widgets.projectsOverview.headers.projectManager',
          value: 'projectManager'
        },
        { text: 'Common.label', value: 'label' },
        {
          text: 'Common.customer',
          value: 'customer',
          truncateUnit: TRUNCATE_UNITS.STRING,
          truncateAfter: 20
        },
        {
          text: 'Common.start',
          value: 'startDate',
          presentWith: dateFormatter.formatDateToLocaleDateStringNoTime
        },
        {
          text: 'Dashboard.widgets.projectsOverview.headers.endDateTarget',
          value: 'endDateTarget',
          presentWith: dateFormatter.formatDateToLocaleDateStringNoTime
        },
        {
          text: 'Common.end',
          value: 'projectEndActual',
          presentWith: dateFormatter.formatDateToLocaleDateStringNoTime
        },
        {
          text: 'Common.status',
          value: 'projectStatus'
        },
        {
          text: 'Dashboard.widgets.projectsOverview.headers.productivity',
          value: 'productivity'
        },
        {
          text: 'Dashboard.widgets.projectsOverview.headers.deleted',
          value: 'isDeleted',
          presentWith: formatBool
        },
        {
          text: 'Dashboard.widgets.projectsOverview.headers.participants',
          value: 'participants',
          presentWith: formatArrayToString
        }
      ],
      withRouterActionLink: true
    },
    {
      id: 'projectsAmount',
      path: 'projects',
      type: 'VALUE',
      getData: response => response.data.totalHits,
      moduleId: 'VOSProjectsOverview'
    }
  ],
  INQUIRY_WIDGETS: [
    {
      id: 'inquiries',
      moduleId: 'projectInquiries',
      path: 'inquiries',
      type: 'TABLE',
      getData: response => response.data.data?.map(data => data.data),
      headers: INQUIRY_HEADERS,
      withRouterActionLink: true
    },
    {
      id: 'inquiriesToday',
      moduleId: 'projectInquiries',
      path: 'inquiries',
      type: 'TABLE',
      defaultFilter: { createdAt: dateService.getToday() },
      getData: response => response.data.data?.map(data => data.data),
      headers: INQUIRY_HEADERS,
      withRouterActionLink: true
    },
    {
      id: 'inquiriesMonth',
      moduleId: 'projectInquiries',
      path: 'inquiries',
      type: 'TABLE',
      defaultFilter: { createdAt: dateService.getFirstDayOfCurrentMonth() },
      getData: response => response.data.data?.map(data => data.data),
      headers: INQUIRY_HEADERS,
      withRouterActionLink: true
    },
    {
      id: 'inquiriesAmount',
      path: 'inquiries',
      type: 'VALUE',
      getData: response => response.data.totalHits,
      moduleId: 'projectInquiries'
    },
    {
      id: 'inquiriesTodayAmount',
      path: 'inquiries',
      type: 'VALUE',
      defaultFilter: { createdAt: dateService.getToday() },
      getData: response => response.data.totalHits,
      moduleId: 'projectInquiries'
    },
    {
      id: 'inquiriesMonthAmount',
      path: 'inquiries',
      type: 'VALUE',
      defaultFilter: { createdAt: dateService.getFirstDayOfCurrentMonth() },
      getData: response => response.data.totalHits,
      moduleId: 'projectInquiries'
    }
  ],
  PRODUCT_WIDGETS: [
    {
      id: 'products',
      moduleId: 'products',
      path: 'products',
      type: 'TABLE',
      getData: response => response.data.data?.map(data => data.data),
      headers: [
        { text: 'Common.matchcode', value: 'matchCode' },
        {
          text: 'Common.label',
          value: 'label'
        },
        {
          text: 'Common.producer',
          value: 'producer'
        },
        {
          text: 'Common.supplier',
          value: 'supplier'
        },
        {
          text: 'Dashboard.widgets.products.headers.baseUnit',
          value: 'baseUnit'
        },
        {
          text: 'Dashboard.widgets.products.headers.basePrice',
          value: 'basePrice',
          presentWith: presentNumberToCurrencyTable,
          alignValue: 'right',
        },
        {
          text: 'Dashboard.widgets.products.headers.categories',
          value: 'categoryNames',
          presentWith: formatArrayToString
        }
      ],
      withRouterActionLink: true
    },
    {
      id: 'productAmount',
      path: 'products',
      type: 'VALUE',
      getData: response => response.data.totalHits,
      moduleId: 'products'
    }
  ]
});

export default PROJECT_MANAGEMENT_WIDGETS;
